import { useId, useState } from 'react';
import type { BreakEvenPivot, Campaign, ProjectionVerdict } from '@clarity/contract';
import { signed } from '../format.js';
import { isAds } from './ProfitModel.js';

/**
 * BreakdownPivot (clarity-campaign-matching task 13.3, Req 9.3 / 7.5) — the
 * inline-expanding "See full breakdown" control + the 2-D break-even heat-map,
 * rebuilt from the agreed mock `brand/campaigns-mock.html` (the `.fb-trigger` +
 * `.breakdown-x` block) in the teal-glass world. The mock is the contract.
 *
 * It renders the server-computed {@link BreakEvenPivot} off the live projection:
 *  - discount campaigns pivot `order value (rows) × discount % (cols)`;
 *  - ads campaigns pivot `cost-per-click (rows) × conversion rate (cols)`;
 *  - the cap (discount) or CPC bid stays a reshaping scalar driven by the 13.2
 *    sliders — because the pivot comes off the SAME recomputed projection, the
 *    grid reshapes live as the slider moves (no local recompute here).
 *
 * Each cell is colored by its `verdict` (win / even / lose) and shows its
 * `value`; the `here:true` cell is flagged "you are here". Accessibility: the
 * trigger is a real button with `aria-expanded`, the grid is a `role="grid"`
 * table whose cells carry the value AND a verdict label (never color alone), and
 * the "you are here" cell announces itself to assistive tech.
 */

const VERDICT_WORD: Record<ProjectionVerdict, string> = {
  win: 'you profit',
  even: 'break even',
  lose: 'you lose',
};

/**
 * The collapsed trigger + the expanding heat-map. Collapsed by default; the
 * button toggles `aria-expanded` and reveals the pivot in place.
 */
export function BreakdownPivot({
  pivot,
  campaign,
  currency = 'JOD',
}: {
  pivot: BreakEvenPivot;
  campaign: Campaign;
  /** Store currency for the money labels in the foot line (defaults to JOD). */
  currency?: string;
}) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const ads = isAds(campaign);

  const sub = ads
    ? 'every cost-per-click × conversion rate — where you win, break even, or lose'
    : 'every order value × discount — where you win, break even, or lose';

  return (
    <div className="fb-trigger" data-slot="breakdown">
      <button
        type="button"
        className="fb-btn"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="fb-btn__label">See full breakdown</span>
        <span className="fb-btn__sub">{sub}</span>
        <svg
          viewBox="0 0 24 24"
          width="16"
          height="16"
          className="fb-btn__chev"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      <div className="breakdown-x" id={panelId} hidden={!open}>
        {open && <PivotGrid pivot={pivot} ads={ads} currency={currency} />}
      </div>
    </div>
  );
}

/**
 * The heat-map itself: a CSS-grid table of `rowAxis.ticks × colAxis.ticks`,
 * each cell `{value, verdict, here}`. The grid is a `role="grid"` so screen
 * readers walk it as a table; the "you are here" cell carries a visible marker
 * AND an `aria-label` so it is not color-dependent.
 */
function PivotGrid({
  pivot,
  ads,
  currency,
}: {
  pivot: BreakEvenPivot;
  ads: boolean;
  currency: string;
}) {
  const cols = pivot.colAxis.ticks.length;
  // 150px row-header column + one equal fraction per data column (mock parity).
  const gridTemplateColumns = `150px repeat(${cols}, 1fr)`;

  const title = ads
    ? 'Profit per order — cost-per-click × conversion rate'
    : 'Profit per order — order value × discount';
  const hint = ads
    ? 'Rows are what you pay per click; columns the share of clicks that become orders. Each cell is profit per order after the ad cost. Your current bid and projected conversion are ringed.'
    : 'Rows are the order value a customer pays; columns the discount you run. Each cell is what you keep per order after the cap, co-funding, commission, fees and food cost. Your typical order and current discount are ringed.';
  const hereKey = ads
    ? 'Your current bid × projected conversion'
    : 'Your typical order × current discount';

  // The column index that holds the "here" cell, so its header can be flagged.
  const hereCol = findHereCol(pivot);

  return (
    <>
      <div className="bx__head">
        <div>
          <h4 className="bx__title">{title}</h4>
          <p className="bx__hint">{hint}</p>
        </div>
      </div>

      <div className="bx__legend" aria-hidden="true">
        <span>
          <i className="sw win" /> You profit
        </span>
        <span>
          <i className="sw even" /> Break even (±0.30)
        </span>
        <span>
          <i className="sw lose" /> You lose
        </span>
        <span className="bx__here-key">
          <i className="sw here" /> {hereKey}
        </span>
      </div>

      <div className="bx__grid-wrap">
        <div
          className="bx__grid"
          role="grid"
          aria-label={title}
          style={{ gridTemplateColumns }}
        >
          {/* Header row: corner + one column head per col-axis tick. */}
          <div className="bx__cell bx__corner" role="columnheader">
            <b>{pivot.rowAxis.label} ↓</b>
            <span>{pivot.colAxis.label} →</span>
          </div>
          {pivot.colAxis.ticks.map((tick, c) => (
            <div
              key={`c-${c}`}
              role="columnheader"
              className={`bx__cell bx__chead${c === hereCol ? ' col-here' : ''}`}
            >
              {tick}
            </div>
          ))}

          {/* One row per row-axis tick. */}
          {pivot.cells.map((row, r) => (
            <PivotRow
              key={`r-${r}`}
              rowLabel={pivot.rowAxis.ticks[r] ?? ''}
              cells={row}
              hereKey={hereKey}
            />
          ))}
        </div>
      </div>

      <p className="bx__foot">{footLine(pivot, ads, currency)}</p>
    </>
  );
}

/** One grid row: a row-header cell followed by its data cells. */
function PivotRow({
  rowLabel,
  cells,
  hereKey,
}: {
  rowLabel: string;
  cells: BreakEvenPivot['cells'][number];
  hereKey: string;
}) {
  return (
    <>
      <div className="bx__cell bx__rhead" role="rowheader">
        {rowLabel}
      </div>
      {cells.map((cell, c) => {
        const verdictWord = VERDICT_WORD[cell.verdict];
        const signedValue = signed(cell.value);
        return (
          <div
            key={`cell-${c}`}
            role="gridcell"
            className={`bx__cell ${cell.verdict}${cell.here ? ' here' : ''}`}
            aria-label={
              cell.here
                ? `${signedValue} — ${verdictWord}. ${hereKey} (you are here).`
                : `${signedValue} — ${verdictWord}`
            }
          >
            <span aria-hidden="true">{signedValue}</span>
            {/* Verdict word carried for every cell so meaning never rides on
                color alone (WCAG 1.4.1); visually it stays a value-only cell. */}
            <span className="sr-only">{` ${verdictWord}`}</span>
            {cell.here && <span className="sr-only"> — you are here</span>}
          </div>
        );
      })}
    </>
  );
}

// ── Pure helpers ────────────────────────────────────────────────────────────

/** The column index carrying the `here` cell, or -1 when none is flagged. */
function findHereCol(pivot: BreakEvenPivot): number {
  for (const row of pivot.cells) {
    const c = row.findIndex((cell) => cell.here);
    if (c !== -1) return c;
  }
  return -1;
}

/**
 * The explanatory foot line under the grid. It reads the "here" cell's row/col
 * from the axis ticks so it stays truthful to the recomputed grid (the mock's
 * "at your current discount / cap, orders below … lose money" read).
 */
function footLine(pivot: BreakEvenPivot, ads: boolean, currency: string): string {
  const here = locateHere(pivot);
  if (!here) {
    return ads
      ? 'The grid shows profit at every bid and conversion rate.'
      : 'The grid shows what you keep per order at every order value and discount.';
  }
  const { rowTick, colTick, cell } = here;
  const verb = cell.value >= 0 ? 'keeps you in profit' : 'loses money';
  return ads
    ? `At your current bid of ${rowTick} ${currency}/click and a ${colTick} conversion rate, each order ${verb} (${signed(cell.value)}). Columns to the right raise conversion; rows below raise the bid.`
    : `At your typical ${rowTick} ${currency} order and a ${colTick} discount, each order ${verb} (${signed(cell.value)}). A tighter cap flattens the high-discount columns for big orders.`;
}

/** Resolve the `here` cell and its row/col tick labels, or null when none. */
function locateHere(
  pivot: BreakEvenPivot,
): { rowTick: string; colTick: string; cell: BreakEvenPivot['cells'][number][number] } | null {
  for (let r = 0; r < pivot.cells.length; r += 1) {
    const row = pivot.cells[r]!;
    for (let c = 0; c < row.length; c += 1) {
      if (row[c]!.here) {
        return {
          rowTick: pivot.rowAxis.ticks[r] ?? '',
          colTick: pivot.colAxis.ticks[c] ?? '',
          cell: row[c]!,
        };
      }
    }
  }
  return null;
}
